//Import MUI components
import {
  Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { Box } from "@mui/system";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
// Lodash library for utility functions
import _ from "lodash";
// Custom components imported from relative paths
import { AddSampleDetails, ItemApprovals } from "../";
// Custom hooks and components from common directory
import { useAuthInfo, ViewImage, ItemRowDetails } from "../../common";
// Custom typography component
import CustomItemStatusTypography from "../../common/components/customMUIComponents/CustomItemStatusTypography.component";

/**
 * SupplierDetails component displays details of suppliers and sample items
 * with interactive functionalities based on user roles.
 * @param {Object} props - Component props including supplier data and callbacks.
 */
export function SupplierDetails({
  supplierValues,
  sampleRequestID,
  sampleItemID,
  setReloadData,
}) {
  // Fetching user authentication information using custom hook
  let decoded = useAuthInfo();

  // Sorting supplier data by qualityReportsID using lodash
  const sortedSupplierValues = _.orderBy(
    supplierValues,
    ["qualityReportsID"],
    ["asc"]
  );

  // 'Edit Item Code' functionality is disabled,
  // but not removed from the codebase in case something breaks,
  // or this functionality is needed again in the future,
  // // State for controlling the visibility of the Edit Item Code Dialog
  // const [openEditItemCodeDialog, setOpenEditItemCodeDialog] = useState(false);
  // // Function to open the Edit Item Code Dialog
  // const handleOpenEditItemCodeDialog = () => {
  //   setOpenEditItemCodeDialog(true);
  // };

  // Render the component
  return (
    <Box>
      {/* Render only if supplierValues array has items */}
      {supplierValues.length > 0 && (
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
        >
          {/*   
            'Edit Item Code' functionality is disabled, 
            but not removed from the codebase in case something breaks, 
            or this functionality is needed again in the future, 
          */}
          {/* Display 'Edit Item Code' button for specific roles */}
          {/* {decoded.role === "sampleControl" && (
            <Grid item xs={12}>
              <Button
                size="small"
                disabled
                variant="contained"
                sx={{
                  padding: "10px 20px",
                  mb: "20px",
                  fontSize: "14px",
                  backgroundColor: "#c7ab79",
                  "&:hover": {
                    backgroundColor: "#c4943f",
                  },
                }}
                // Click handler to open dialog
                onClick={handleOpenEditItemCodeDialog}
              >
                Edit Item Code
              </Button>
            </Grid>
          )} */}
          <Grid item xs={12}>
            <Typography variant="h6" component="h5" gutterBottom mt={4}>
              <b>All Sample Details</b>
            </Typography>
          </Grid>
          {/*  Map through sorted supplier values */}
          {sortedSupplierValues.map((obj, index) => {
            let {
              primaryProductImages,
              secondaryProductImages,
              userApproval,
              execChefApproval,
              HODApproval,
              erpId,
              erpName,
              deliveredDate,
            } = obj;

            //!? TODO: refactor sample data to be reuseable
            // Create sample data object
            let sampleData = {
              qualityReportsID: obj.qualityReportsID,
              newItemERPCode: obj.newItemERPCode ? obj.newItemERPCode : "N/A",
              receivedItemDescription: obj.receivedItemDescription
                ? obj.receivedItemDescription
                : "N/A",
              receivedProductClass: obj.receivedProductClass
                ? obj.receivedProductClass
                : "N/A",
              brand: obj.brand,
              countryOfOrigin: obj.countryOfOrigin,
              packaging: obj.packaging,
              productionDate: obj.productionDate,
              expiryDate: obj.expiryDate,
              receiptDate: obj.receiptDate,
              dateSupplierAdded: obj.dateSupplierAdded,
              dateModified: obj.dateModified,
            };
            // Create supplier Data object
            let supplierData = {
              supplierName: obj.name,
              supplierEmail: obj.email,
            };
            //Create item approvals object
            let itemApprovals = {
              userApproval: userApproval,
              execChefApproval: execChefApproval,
              HODApproval: HODApproval,
            };
            // Create receiver details object
            let receiverDetails = {
              erpId: erpId,
              erpName: erpName,
              date: deliveredDate,
            };
            // Create packaging images array
            let packagingImages = [
              {
                title: "Primary Packaging Image(s)",
                images: primaryProductImages,
              },
              {
                title: "Secondary Packaging Image(s)",
                images: secondaryProductImages,
              },
            ];

            return (
              <Accordion
                sx={{
                  mt: 2,
                  width: "99%",
                }}
                key={index}
              >
                {/* Accordion summary has status widget and supplier name */}
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Grid container>
                    <Grid item xs={12}>
                      <Typography>
                        Supplier {index + 1}:{" "}
                        {(decoded.role === "sampleControl" ||
                          decoded.role === "sampleControlView" ||
                          decoded.role === "procurement") &&
                          supplierData.supplierName}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sx={{ display: { md: "flex" } }} mt={1}>
                      <CustomItemStatusTypography
                        title="Item Receipt:"
                        status={
                          receiverDetails.erpName === "pending"
                            ? "pending"
                            : "Accepted"
                        }
                      />

                      {itemApprovals.userApproval && (
                        <CustomItemStatusTypography
                          title="Chef Approval:"
                          status={itemApprovals.userApproval.status}
                        />
                      )}
                      {itemApprovals.userApproval?.status !== "Rejected" && (
                        <>
                          <CustomItemStatusTypography
                            title="Executive Chef Approval:"
                            status={itemApprovals.execChefApproval.status}
                          />
                          {itemApprovals.execChefApproval.status !==
                            "Rejected" && (
                            <CustomItemStatusTypography
                              title=" HOD Chef Approval:"
                              status={itemApprovals.HODApproval.status}
                            />
                          )}
                        </>
                      )}
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  {/* Add and edit sample details components visible to specific roles */}
                  {decoded.role === "sampleControl" && (
                    <>
                      <AddSampleDetails
                        supplier={obj}
                        sampleRequestID={sampleRequestID}
                        sampleItemID={sampleItemID}
                        supplierValues={supplierValues}
                        setReloadData={setReloadData}
                      />

                      {/*   
                        'Edit Sample Details' functionality is disabled, 
                        but not removed from the codebase in case something breaks, 
                        or this functionality is needed again in the future, 
                      */}
                      {/* <EditSampleDetails
                        supplier={obj}
                        sampleRequestID={sampleRequestID}
                        sampleItemID={sampleItemID}
                        supplierValues={supplierValues}
                        setReloadData={setReloadData}
                      /> */}
                    </>
                  )}

                  {/* Supplier name and email details visible to specific roles */}
                  {(decoded.role === "sampleControl" ||
                    decoded.role === "sampleControlView" ||
                    decoded.role === "procurement") && (
                    <ItemRowDetails data={supplierData} />
                  )}

                  {/* Sample Details  */}
                  <Typography
                    style={{
                      fontSize: "16px",
                      marginBottom: "15px",
                      marginTop: "10px",
                    }}
                    gutterBottom
                  >
                    <b>Sample Details:</b>
                  </Typography>

                  <ItemRowDetails data={sampleData} />
                  {/* Primary and secondary packaging images */}
                  {packagingImages.map((imagesObject, index) => {
                    return (
                      <Grid
                        key={index}
                        container
                        style={{
                          fontSize: "14px",
                          marginBottom: "15px",
                        }}
                      >
                        <Grid item xs={12} sm={5}>
                          <span>{imagesObject.title}</span>
                        </Grid>

                        <Grid item xs={12} sm={7}>
                          {imagesObject.images &&
                          imagesObject.images !== "pending" ? (
                            imagesObject.images.map((imageObject, index) => {
                              return (
                                <ViewImage
                                  key={index}
                                  productImage={imageObject}
                                />
                              );
                            })
                          ) : (
                            <span>pending</span>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })}
                  {/* Receiver Details */}
                  <Grid
                    container
                    style={{
                      fontSize: "14px",
                      marginBottom: "15px",
                    }}
                  >
                    <Typography
                      style={{
                        fontSize: "16px",
                        marginBottom: "15px",
                        marginTop: "10px",
                      }}
                      gutterBottom
                    >
                      <b>Receiver Details:</b>
                    </Typography>
                    <ItemRowDetails data={receiverDetails} />
                  </Grid>
                  {/* Approval details */}
                  <ItemApprovals
                    itemApprovals={itemApprovals}
                    supplier={obj}
                    supplierValues={supplierValues}
                    sampleRequestID={sampleRequestID}
                    sampleItemID={sampleItemID}
                    setReloadData={setReloadData}
                  />
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Grid>
      )}

      {/*   
        'Edit Item Code' functionality is disabled, 
        but not removed from the codebase in case something breaks, 
        or this functionality is needed again in the future, 
      */}
      {/* <EditItemERPCodeDialog
        open={openEditItemCodeDialog}
        setOpen={setOpenEditItemCodeDialog}
        handleOpen={handleOpenEditItemCodeDialog}
        sampleRequestID={sampleRequestID}
        sampleItemID={sampleItemID}
        setReloadData={setReloadData}
      /> */}
    </Box>
  );
}
